/**
 * Clasificador de fallos de Auth (GoTrue) que son error del usuario, no bug.
 * Lo comparten negocio, portal y rider para decidir qué va a Sentry como
 * issue y qué queda como breadcrumb/toast (evita alert fatigue por typos).
 *
 * `validation_failed` NO está acá a propósito: post-validación client-side
 * un 400 residual significa divergencia del gate o cliente viejo — se reporta.
 */

const EXPECTED_AUTH_CODES = new Set([
  'invalid_credentials', // login: mail/pass incorrectos
  'email_not_confirmed',
  'user_already_registered',
  'email_exists',
  'phone_exists',
  'signup_disabled',
  'weak_password',
  'same_password',
  'user_banned',
  'otp_expired',
  'otp_disabled',
  'captcha_failed',
  'over_request_rate_limit',
  'over_email_send_rate_limit',
  'over_sms_send_rate_limit',
  'email_provider_disabled',
  'phone_provider_disabled',
  'identity_blocked', // check_identity_allowed (no es GoTrue, lo inyecta el caller)
  // Sesión / flow-state: link vencido, token consumido, state OAuth expirado.
  'refresh_token_not_found',
  'session_not_found',
  'flow_state_not_found',
  'flow_state_expired',
  'bad_oauth_state',
  'bad_oauth_callback',
  'bad_code_verifier',
  'reauthentication_needed',
]);

const EXPECTED_AUTH_MESSAGE_FRAGMENTS = [
  'already registered',
  'already been registered',
  'invalid login credentials',
  'invalid credentials',
  'email not confirmed',
  'rate limit',
  'too many',
  'password should be at least',
  'access_denied', // query param de redirect OAuth
  'invalid or has expired', // link de mail vencido
  // SSO: el usuario canceló el flujo en el browser
  'cancelado',
  'canceled',
  'cancelled',
  'dismissed',
  // Sesión / tokens: esperado que venzan; no es crash.
  'jwt expired',
  'invalid jwt',
  'auth session missing',
  'not authenticated',
  'session required',
  'refresh token',
  // Red / offline (regla error-mapping: no se captura como crash).
  'networkerror',
  'a network error occurred',
  'failed to fetch',
  'fetch failed',
  'unknownhostexception',
  'unable to resolve host',
  'network request failed',
  'failed to send a request',
  'err_network',
  'load failed',
  'timeout',
  'timed out',
  'connection refused',
  'connection reset',
  'aborterror',
  'the operation was aborted',
];

function authErrorText(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`;
  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message === 'string') return message;
  return String(error ?? '');
}

/**
 * True si el fallo de auth es esperado (typo del usuario, cuenta existente,
 * rate limit, cancelación SSO, link vencido). False = inesperado → Sentry.
 */
export function isExpectedAuthFailure(error: unknown): boolean {
  if (error == null) return false;
  const code = String(
    (error as { code?: unknown; error_code?: unknown } | null)?.code ??
      (error as { error_code?: unknown } | null)?.error_code ??
      '',
  ).toLowerCase();
  if (code && EXPECTED_AUTH_CODES.has(code)) return true;
  const status = (error as { status?: unknown } | null)?.status;
  const text = authErrorText(error).toLowerCase();
  // Un apikey roto/inválido también responde 401 — eso sí es incidente, no esperado.
  const isApiKeyProblem = text.includes('apikey') || text.includes('api key');
  if (!isApiKeyProblem && (status === 429 || status === 401)) return true;
  return EXPECTED_AUTH_MESSAGE_FRAGMENTS.some((fragment) => text.includes(fragment));
}
